/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, December 2021
 * @format
 * @flow
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, NoChecks } from './Divider';
import CheckCard from './CheckCard';
import classes from './CheckCard.module.css';
import FireboardUtils from '../../../../util/fireboard-util';
import CommonUtil from './utils';
import modeEnums from '../../../../enums/modeEnums';

const SOURCETYPE = 'sourceType';
const ORDERTYPE = 'orderType';
const REVENUECENTER = 'revenueCenter';

const CheckGroups = props => {
  const {
    checks,
    managerMode,
    checkFilters,
    storeInfo,
    setSelectedCheck,
    selectedItem,
    onHandleSms,
    updateOrderStatus,
    deviceGroup,
    currentMode,
    viewGuestLocationOnMap,
    onOrderDelivered,
  } = props;
  const [groupChecks, setGroupChecks] = useState(new Map());

  const filterChecks = () => {
    const { activeCheckFilter } = checkFilters;
    let checksMap = new Map();
    if (!managerMode) {
      switch (activeCheckFilter) {
        case SOURCETYPE:
          checksMap = filterBasedOnSourceType();
          break;
        case ORDERTYPE:
          checksMap = filterBasedOnOrderType();
          break;
        case REVENUECENTER:
          checksMap = filterBasedOnRevenueCenter();
          break;
        default: break;
      }
      setGroupChecks(checksMap);
    }
  };

  const filterBasedOnSourceType = () => {
    const { orderType } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      checks.forEach(check => {
        const sourceTypeName = FireboardUtils.getOrderTypeFromSourceType(check.sourceType);
        if (sourceTypeName) {
          const existingChecks = checksMap.has(sourceTypeName) ? checksMap.get(sourceTypeName) : [];
          const newChecks = [...existingChecks, check];
          checksMap.set(sourceTypeName, newChecks);
        }
      });
    } else {
      checksMap.set(orderType, [...checks]);
    }

    return checksMap;
  };

  const filterBasedOnOrderType = () => {
    const { orderType } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      const { orderTypes } = storeInfo;
      let orderTypesLookup = {};
      orderTypes.forEach(eachType => orderTypesLookup[eachType.id] = eachType.name);
      checks.forEach(check => {
        const orderTypeName = orderTypesLookup[check.orderTypeId];
        if (orderTypeName) {
          const existingChecks = checksMap.has(orderTypeName) ? checksMap.get(orderTypeName) : [];
          const newChecks = [...existingChecks, check];
          checksMap.set(orderTypeName, newChecks);
        }
      });
    } else {
      checksMap.set(orderType, [...checks]);
    }

    return checksMap;
  };

  const filterBasedOnRevenueCenter = () => {
    const { orderType } = checkFilters;
    let checksMap = new Map();
    if (orderType === 'All') {
      const { revenueCenters } = storeInfo;
      let revenueCentersLookup = {};
      revenueCenters.forEach(eachType => revenueCentersLookup[eachType.id] = eachType.name);
      checks.forEach(check => {
        const revenueCenterName = revenueCentersLookup[check.revenueCenterId];
        if (revenueCenterName) {
          const existingChecks = checksMap.has(revenueCenterName) ? checksMap.get(revenueCenterName) : [];
          const newChecks = [...existingChecks, check];
          checksMap.set(revenueCenterName, newChecks);
        }
      })
    } else {
      checksMap.set(orderType, [...checks]);
    }

    return checksMap;
  };

  useEffect(() => {
    filterChecks();
  }, [checks, managerMode, checkFilters]);

  return (
    <div>
      {Array.from(groupChecks.entries()).map(([key, checkArray]) =>
        <React.Fragment key={key}>
          <Divider title={key} />
          <div className={classes.checkCardsWrapper}>
            {checkArray.length === 0 ? <NoChecks /> : (
              checkArray.map(check =>
                <CheckCard
                  key={check.id}
                  check={check}
                  checkConfig={CommonUtil.getCheckConfig(
                    check,
                    currentMode,
                    storeInfo,
                    setSelectedCheck,
                    onHandleSms,
                    updateOrderStatus,
                    viewGuestLocationOnMap,
                    onOrderDelivered
                  )}
                  onSelectCheck={setSelectedCheck}
                  isCheckSelected={selectedItem.id === check.id}
                  deviceGroup={deviceGroup}
                  currentMode={currentMode}
                />
              )
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default CheckGroups;

CheckGroups.propTypes = {
  checks: PropTypes.array.isRequired,
  managerMode: PropTypes.bool,
  setSelectedCheck: PropTypes.func,
  selectedItem: PropTypes.object,
  checkFilters: PropTypes.object,
  storeInfo: PropTypes.object,
  onHandleSms: PropTypes.func,
  updateOrderStatus: PropTypes.func,
  deviceGroup: PropTypes.object,
  currentMode: PropTypes.string,
  viewGuestLocationOnMap: PropTypes.func,
  onOrderDelivered: PropTypes.func,
};

CheckGroups.defaultProps = {
  checks: [],
  managerMode: false,
  setSelectedCheck: () => { },
  selectedItem: {},
  checkFilters: {},
  storeInfo: {},
  onHandleSms: () => { },
  updateOrderStatus: () => { },
  deviceGroup: null,
  currentMode: modeEnums.TABLE_DELIVERY,
  viewGuestLocationOnMap: () => { },
  onOrderDelivered: () => { },
};
